import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";
import {
  Sparkles, Plus, Calendar, MapPin, Users, ChevronRight,
  Loader2, Clock, CheckCircle2, AlertCircle, Target
} from "lucide-react";
import { toast } from "sonner";

const STAGE_COLOR = {
  DRAFT: "text-[#706E6B] bg-[#FAFAFA]",
  DISCOVERY: "text-[#0176D3] bg-[#EEF4FF]",
  BRIEF_READY: "text-[#0176D3] bg-[#EEF4FF]",
  BLUEPRINT_PROPOSED: "text-[#5E4DB2] bg-[#F1ECFF]",
  REVIEW_REQUIRED: "text-[#8C4B00] bg-[#FFF4E5]",
  APPROVED: "text-[#2E844A] bg-[#EAF5EE]",
  ACTIVATED: "text-[#0176D3] bg-[#EEF4FF]",
};

const STAGE_ICON = {
  DRAFT: Clock,
  DISCOVERY: Sparkles,
  BRIEF_READY: CheckCircle2,
  BLUEPRINT_PROPOSED: Sparkles,
  REVIEW_REQUIRED: AlertCircle,
  APPROVED: CheckCircle2,
  ACTIVATED: CheckCircle2,
};

export default function ImagineDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/imagine/events")
      .then(r => setEvents(r.data.events || []))
      .catch(() => toast.error("Failed to load events"))
      .finally(() => setLoading(false));
  }, []);

  const staged = (stage) => events.filter(e => e.planning_stage === stage);
  const activatedCount = events.filter(e => e.planning_stage === "ACTIVATED").length;
  const inProgress = events.filter(e => !["ACTIVATED", "CANCELLED"].includes(e.planning_stage)).length;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#5E4DB2]" />
            <h1 className="text-2xl font-bold text-[#181818]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              ONE Imagine
            </h1>
          </div>
          <p className="text-[#706E6B] text-sm">Agentic Event Discovery &amp; Blueprint</p>
        </div>
        <button
          data-testid="imagine-to-leads-btn"
          onClick={() => navigate("/engage/leads")}
          className="flex items-center gap-2 px-4 py-2 bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30 rounded-lg text-sm font-medium hover:bg-[#EEF4FF] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Start from Lead
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Events", value: events.length, color: "text-[#181818]" },
          { label: "In Planning", value: inProgress, color: "text-[#8C4B00]" },
          { label: "Activated", value: activatedCount, color: "text-[#0176D3]" },
          { label: "Blueprint Ready", value: staged("APPROVED").length + activatedCount, color: "text-[#2E844A]" },
        ].map(stat => (
          <div key={stat.label} className="bg-white border border-[#E5E5E5] rounded-xl p-4">
            <p className="text-xs text-[#706E6B] uppercase tracking-wide mb-1">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Event list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-[#706E6B] mx-auto mb-3" />
          <p className="text-[#706E6B] text-sm">No events in planning yet.</p>
          <p className="text-[#706E6B] text-xs mt-1">
            Convert a QUALIFIED lead to start the planning journey.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => {
            const StageIcon = STAGE_ICON[event.planning_stage] || Clock;
            return (
              <div
                key={event.id}
                data-testid={`imagine-event-${event.id}`}
                className="bg-white border border-[#E5E5E5] rounded-xl p-4 hover:border-[#C9C9C9] transition-colors cursor-pointer"
                onClick={() => navigate(`/imagine/events/${event.id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-[#706E6B]">{event.event_number}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${STAGE_COLOR[event.planning_stage] || "text-[#706E6B] bg-[#FAFAFA]"}`}>
                        <StageIcon className="w-3 h-3" />
                        {event.planning_stage?.replace(/_/g, " ")}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-[#181818] truncate">{event.name}</h3>
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      {event.event_type_name && (
                        <span className="text-xs text-[#706E6B]">{event.event_type_name}</span>
                      )}
                      {event.event_date && (
                        <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                          <Calendar className="w-3 h-3" />
                          {event.event_date}
                        </span>
                      )}
                      {event.location_city && (
                        <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                          <MapPin className="w-3 h-3" />
                          {event.location_city}
                        </span>
                      )}
                      {event.guest_count && (
                        <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                          <Users className="w-3 h-3" />
                          {event.guest_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {event.threads_activated && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF4FF] text-[#0176D3] font-medium">
                        THREADS LIVE
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-[#706E6B]" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
